import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import toast from 'react-hot-toast';

dayjs.extend(customParseFormat);

const helpingMethod = {
  showToaster: (message: string, type: 'success' | 'error' = 'error'): void => {
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      default:
        break;
    }
  },

  formatChipLabel: (label: string): string => {
    if (label == null) return '';
    const normalized = String(label).replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

    return normalized
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  formatDate: (date: string | Date): string => {
    if (!date) return '';
    // Try DD/MM/YYYY first (what FormDatePicker produces) to avoid
    // misinterpretation as MM/DD/YYYY by native Date parser
    if (typeof date === 'string') {
      const parsed = dayjs(date, 'DD/MM/YYYY', true);
      if (parsed.isValid()) return parsed.format('DD/MM/YYYY');
    }
    const d = dayjs(date);
    return d.isValid() ? d.format('DD/MM/YYYY') : '';
  },

  safeParseInt: (value: any): number | null => {
    if (!value && value !== 0) return null;
    const parsed = parseInt(String(value), 10);
    return isNaN(parsed) ? null : parsed;
  },

  hasValue: (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    const numValue = Number(value);
    return !isNaN(numValue) && numValue !== 0;
  },

  toNumber: (value: any): number | undefined => {
    if (helpingMethod.hasValue(value)) {
      return Number(value);
    }
    return undefined;
  },

  safeParseIntForPayload: (value: any): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = parseInt(String(value));
    return isNaN(parsed) || parsed === 0 ? undefined : parsed;
  },

  getChipType: (label: string): string => {
    const normalized = (label || '').toUpperCase().trim();
    if (['STANDARD', 'MONTHLY', 'STD'].includes(normalized)) return 'STANDARD';
    if (['ACQUISITION', 'ACQ', 'ADJUSTMENT', 'ADJUSTING', 'S&P PURCHASE'].includes(normalized))
      return 'ACQUISITION';
    if (['YES', 'ACTIVE'].includes(normalized)) return 'SUCCESS';
    if (['NO', 'INACTIVE'].includes(normalized)) return 'ERROR';
    if (['EOM'].includes(normalized)) return 'DANGER';
    if (['NEW BUILDING', 'NEW', 'NEW_BUILDING', 'NEWBUILDING'].includes(normalized)) return 'NEW';
    if (['2HA'].includes(normalized)) return 'WARNING';
    return normalized;
  },

  getChipColors: (
    label: string,
    theme: any,
    colors?: Partial<{
      BLUE: string;
      PURPLE: string;
      TEAL: string;
      PINK_BG: string;
      PINK_TEXT: string;
      RED: string;
      GREEN: string;
      ORANGE: string;
      DEFAULT_BG: string;
      DEFAULT_TEXT: string;
    }>,
  ): { bg: string | undefined; text: string | undefined | ((_theme: any) => string) } => {
    const type = helpingMethod.getChipType(label);
    const whiteText = (t: any) => t.palette.common.white;

    const COLORS = {
      BLUE: colors?.BLUE,
      PURPLE: colors?.PURPLE,
      TEAL: colors?.TEAL,
      PINK_BG: colors?.PINK_BG,
      PINK_TEXT: colors?.PINK_TEXT,
      RED: colors?.RED,
      GREEN: colors?.GREEN,
      ORANGE: colors?.ORANGE,
      DEFAULT_BG: colors?.DEFAULT_BG,
      DEFAULT_TEXT: colors?.DEFAULT_TEXT,
    };

    switch (type) {
      case 'STANDARD':
        return { bg: COLORS.BLUE, text: whiteText };
      case 'ACQUISITION':
        return { bg: COLORS.PURPLE, text: whiteText };
      case 'SUCCESS':
        return { bg: COLORS.TEAL, text: whiteText };
      case 'ERROR':
        return { bg: COLORS.PINK_BG, text: COLORS.PINK_TEXT };
      case 'DANGER':
        return { bg: COLORS.RED, text: whiteText };
      case 'NEW':
        return { bg: COLORS.GREEN, text: whiteText };
      case 'WARNING':
        return { bg: COLORS.ORANGE, text: whiteText };
      default:
        return { bg: COLORS.DEFAULT_BG, text: COLORS.DEFAULT_TEXT };
    }
  },
};

export { helpingMethod };
